import type {
  Manifest,
  MintQuote,
  MintTier,
  MintingStatus,
  ResolutionEnvelope,
  WalletPortfolio
} from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(path, { ...init, headers });
  const body = await response.json() as T | { error?: { code?: string; message?: string } };
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(response.status, error?.code ?? "request_failed", error?.message ?? "Request failed");
  }
  return body as T;
}

export const api = {
  mintingStatus: () => request<MintingStatus>("/v1/mint/config"),
  availability: (qyoo: string) =>
    request<{ qyoo: number; available: boolean }>(`/v1/mint/availability/${encodeURIComponent(qyoo)}`),
  quote: (recipient: string, tier: MintTier, qyoo?: string) =>
    request<MintQuote>("/v1/mint/quote", {
      method: "POST",
      body: JSON.stringify({ recipient, tier, ...(qyoo === undefined ? {} : { qyoo }) })
    }),
  portfolio: (wallet: string) =>
    request<WalletPortfolio>(`/v1/wallet/${encodeURIComponent(wallet)}/qyoos`),
  qyoo: (qyoo: number) => request<ResolutionEnvelope>(`/v1/qyoo/${qyoo}`),
  challenge: (qyoo: number, address: string, manifest: Manifest) =>
    request<{ message: string; expiresAt: string }>("/v1/auth/challenge", {
      method: "POST",
      body: JSON.stringify({ qyoo, address, manifest })
    }),
  publish: (qyoo: number, message: string, signature: string, manifest: Manifest) =>
    request<{ revision: number; manifestHash: string; role: "owner" | "controller" }>(
      `/v1/qyoo/${qyoo}/experience`,
      { method: "PUT", body: JSON.stringify({ message, signature, manifest }) }
    )
};
